import { ChangeDetectionStrategy, Component, computed, effect, input } from '@angular/core';
import { textStyleClass, TextStyleSize } from '@sirius/typography';

import { IChip } from '../_shared/models/chip.model';
import { ChipSize, DeprecatedChipSize } from '../_shared/types/chip-size.type';
import { ChipAttention, ChipType, DeprecatedChipAttention } from '../_shared/types/chip.type';

@Component({
    selector: 'sirius-chip',
    template: `
        <span>
            {{ chipText() }}
        </span>
    `,
    styleUrls: ['./chip.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        'class': 'sir-chip',
        '[class]': 'textClass()',
        '[class.is-solid]': 'resolvedType() === "solid"',
        '[class.is-outline]': 'resolvedType() === "outline"',
        '[class.is-high]': 'resolvedAttention() === "high"',
        '[class.is-medium]': 'resolvedAttention() === "medium"',
        '[class.is-positive]': 'resolvedAttention() === "positive"',
        '[class.is-neutral]': 'resolvedAttention() === "neutral"',
        '[class.is-brand]': 'resolvedAttention() === "brand"',
        '[class.is-guide]': 'resolvedAttention() === "guide"',
        '[class.is-e-series]': 'resolvedAttention() === "eSeries"',
        '[class.is-ur-series]': 'resolvedAttention() === "urSeries"',
        '[class.is-g-series]': 'resolvedAttention() === "gSeries"',
        '[class.is-small]': 'resolvedSize() === "s"',
        '[class.is-large]': 'resolvedSize() === "l"',
        '[attr.data-chip-type]': 'resolvedType()',
        '[attr.data-chip-attention]': 'resolvedAttention()',
        '[attr.data-chip-size]': 'resolvedSize()',
    },
})
export class ChipComponent {
    readonly config = input<IChip | undefined>();
    readonly type = input<ChipType>('solid');
    readonly attention = input<ChipAttention>('high');
    readonly size = input<ChipSize>('s');
    readonly text = input('');

    /** @deprecated Use `size` with 's' or 'l' instead. Will be removed in a future major version. */
    readonly chipSize = input<DeprecatedChipSize | undefined>();

    /** @deprecated Use `attention` instead. Will be removed in a future major version. */
    readonly chipType = input<DeprecatedChipAttention | undefined>();

    private readonly deprecationWarnings = new Set<string>();

    constructor() {
        effect(() => {
            if (this.chipSize() !== undefined) this.warnDeprecated('chipSize', 'size');
            if (this.chipType() !== undefined) this.warnDeprecated('chipType', 'attention');
            if (this.config()?.chipType !== undefined) this.warnDeprecated('config.chipType', 'config.attention');
        });
    }

    readonly resolvedType = computed<ChipType>(() => this.config()?.type ?? this.type());

    readonly resolvedAttention = computed<ChipAttention>(() => {
        const config = this.config();
        return config?.attention ?? this.normalizeAttention(config?.chipType ?? this.chipType() ?? this.attention());
    });

    readonly resolvedSize = computed<ChipSize>(
        () => this.config()?.size ?? this.normalizeSize(this.chipSize() ?? this.size())
    );

    readonly chipText = computed(() => this.config()?.text ?? this.text());

    readonly textStyleSize = computed<TextStyleSize>(() => (this.resolvedSize() === 's' ? 's' : 'm'));

    /** Typography utility class for the chip label (ADR 0001). */
    readonly textClass = computed(() => textStyleClass('system', this.textStyleSize(), 'default'));

    private normalizeAttention(attention: DeprecatedChipAttention | ChipAttention): ChipAttention {
        if (attention === 'primary') return 'positive';
        if (attention === 'secondary') return 'brand';
        if (attention === 'tertiary') return 'neutral';

        return attention;
    }

    private normalizeSize(size: ChipSize | DeprecatedChipSize): ChipSize {
        if (size === 'small') return 's';
        if (size === 'large') return 'l';

        return size;
    }

    private warnDeprecated(oldName: string, newName: string): void {
        if (this.deprecationWarnings.has(oldName)) return;
        this.deprecationWarnings.add(oldName);
        console.warn(`[ChipComponent] "${oldName}" is deprecated. Use "${newName}" instead.`);
    }
}
